import React, { useState, useEffect } from 'react';
import { X, Building2, MapPin, DollarSign, FileText } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const PropertyFormModal = ({ property, communes, onSave, onClose }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    address: '',
    commune_id: '',
    property_type: '',
    status: 'available',
    price: '',
    bedrooms: '',
    bathrooms: '',
    surface_area: '',
    description: '',
    features: {},
    images: [],
    videos: []
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (property) {
      setFormData({
        title: property?.title || '',
        address: property?.address || '',
        commune_id: property?.commune_id || '',
        property_type: property?.property_type || '',
        status: property?.status || 'available',
        price: property?.price?.toString() || '',
        bedrooms: property?.bedrooms?.toString() || '',
        bathrooms: property?.bathrooms?.toString() || '',
        surface_area: property?.surface_area?.toString() || '',
        description: property?.description || '',
        features: property?.features || {},
        images: property?.images || [],
        videos: property?.videos || []
      });
    }
  }, [property]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFeatureChange = (feature, checked) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev?.features,
        [feature]: checked
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData?.address?.trim()) {
      newErrors.address = 'L\'adresse est requise';
    }

    if (!formData?.commune_id) {
      newErrors.commune_id = 'La commune est requise';
    }

    if (!formData?.property_type) {
      newErrors.property_type = 'Le type de propriété est requis';
    }

    if (!formData?.price || isNaN(parseFloat(formData?.price))) {
      newErrors.price = 'Le prix doit être un nombre valide';
    }

    if (formData?.bedrooms && isNaN(parseInt(formData?.bedrooms))) {
      newErrors.bedrooms = 'Le nombre de chambres doit être un nombre';
    }

    if (formData?.bathrooms && isNaN(parseInt(formData?.bathrooms))) {
      newErrors.bathrooms = 'Le nombre de salles de bain doit être un nombre';
    }

    if (formData?.surface_area && isNaN(parseFloat(formData?.surface_area))) {
      newErrors.surface_area = 'La surface doit être un nombre';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    
    const submitData = {
      ...formData,
      price: parseFloat(formData?.price),
      bedrooms: formData?.bedrooms ? parseInt(formData?.bedrooms) : null,
      bathrooms: formData?.bathrooms ? parseInt(formData?.bathrooms) : null,
      surface_area: formData?.surface_area ? parseFloat(formData?.surface_area) : null,
      agent_id: userProfile?.id, // Set current user as agent if not specified
      owner_id: property?.owner_id || userProfile?.id // Keep existing owner or set current user
    };

    const success = await onSave?.(submitData);
    
    if (success) {
      onClose?.();
    }
    
    setSaving(false);
  };

  const propertyTypes = [
    { value: 'apartment', label: 'Appartement' },
    { value: 'house', label: 'Maison' },
    { value: 'villa', label: 'Villa' },
    { value: 'land', label: 'Terrain' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const statuses = [
    { value: 'available', label: 'Disponible' },
    { value: 'reserved', label: 'Réservé' },
    { value: 'sold', label: 'Vendu' },
    { value: 'withdrawn', label: 'Retiré' }
  ];

  const features = [
    'pool', 'garden', 'parking', 'security', 'furnished', 'elevator',
    'balcony', 'terrace', 'fireplace', 'air_conditioning', 'gym', 'laundry'
  ];

  const getFeatureLabel = (feature) => {
    const labels = {
      pool: 'Piscine',
      garden: 'Jardin',
      parking: 'Parking',
      security: 'Sécurité',
      furnished: 'Meublé',
      elevator: 'Ascenseur',
      balcony: 'Balcon',
      terrace: 'Terrasse',
      fireplace: 'Cheminée',
      air_conditioning: 'Climatisation',
      gym: 'Salle de sport',
      laundry: 'Buanderie'
    };
    return labels?.[feature] || feature;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-blue-600" />
            {property ? 'Modifier la propriété' : 'Nouvelle propriété'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre de la propriété *
              </label>
              <input
                type="text"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: Villa moderne avec piscine"
              />
              {errors?.title && <p className="mt-1 text-sm text-red-600">{errors?.title}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de propriété *
              </label>
              <select
                value={formData?.property_type}
                onChange={(e) => handleInputChange('property_type', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.property_type ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner un type</option>
                {propertyTypes?.map(type => (
                  <option key={type?.value} value={type?.value}>{type?.label}</option>
                ))}
              </select>
              {errors?.property_type && <p className="mt-1 text-sm text-red-600">{errors?.property_type}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <select
                value={formData?.status}
                onChange={(e) => handleInputChange('status', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {statuses?.map(status => (
                  <option key={status?.value} value={status?.value}>{status?.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                Adresse *
              </label>
              <input
                type="text"
                value={formData?.address}
                onChange={(e) => handleInputChange('address', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.address ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: Rue des Jardins, Cocody"
              />
              {errors?.address && <p className="mt-1 text-sm text-red-600">{errors?.address}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commune *
              </label>
              <select
                value={formData?.commune_id}
                onChange={(e) => handleInputChange('commune_id', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.commune_id ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner une commune</option>
                {communes?.map(commune => (
                  <option key={commune?.id} value={commune?.id}>
                    {commune?.name} - {commune?.district}
                  </option>
                ))}
              </select>
              {errors?.commune_id && <p className="mt-1 text-sm text-red-600">{errors?.commune_id}</p>}
            </div>
          </div>

          {/* Details */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <DollarSign className="h-4 w-4 mr-1" />
                Prix (FCFA) *
              </label>
              <input
                type="number"
                value={formData?.price}
                onChange={(e) => handleInputChange('price', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.price ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: 50000000"
              />
              {errors?.price && <p className="mt-1 text-sm text-red-600">{errors?.price}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chambres
              </label>
              <input
                type="number"
                value={formData?.bedrooms}
                onChange={(e) => handleInputChange('bedrooms', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.bedrooms ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: 3"
              />
              {errors?.bedrooms && <p className="mt-1 text-sm text-red-600">{errors?.bedrooms}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salles de bain
              </label>
              <input
                type="number"
                value={formData?.bathrooms}
                onChange={(e) => handleInputChange('bathrooms', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.bathrooms ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: 2"
              />
              {errors?.bathrooms && <p className="mt-1 text-sm text-red-600">{errors?.bathrooms}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Surface (m²)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData?.surface_area}
                onChange={(e) => handleInputChange('surface_area', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.surface_area ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: 150"
              />
              {errors?.surface_area && <p className="mt-1 text-sm text-red-600">{errors?.surface_area}</p>}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FileText className="h-4 w-4 mr-1" />
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Description détaillée de la propriété..."
            />
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Caractéristiques</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {features?.map(feature => (
                <label key={feature} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData?.features?.[feature] || false}
                    onChange={(e) => handleFeatureChange(feature, e?.target?.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{getFeatureLabel(feature)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Sauvegarde...' : property ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyFormModal;